import { useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useAdminListState } from '../context/AdminListStateContext'
import { createPersonalViewsPreferenceStore, personalViewsPreferenceKey } from '../services/interfacePreferences'
import { normalizeViewConfiguration, normalizePersonalView, type ViewConfiguration, type ViewOperationError } from '../services/tableViews'
import type { PersonalTableViewV1, TableDefinition, TableFilterValue, TablePreferenceScope } from '../services/tableState'

export function usePersonalTableViews<F extends Record<string, TableFilterValue>>(options: {
  definition: TableDefinition<F>; scope: TablePreferenceScope | null; revision: string; enabled: boolean
  configuration: ViewConfiguration; transientCriteria: boolean
  apply: (configuration: ViewConfiguration) => void
}) {
  const lists = useAdminListState(), { definition } = options
  const key = personalViewsPreferenceKey(options.enabled ? options.scope : null)
  const store = useMemo(()=>createPersonalViewsPreferenceStore(key,definition),[key,definition,options.revision])
  const snapshot = useSyncExternalStore(store.subscribe,store.getSnapshot,store.getSnapshot)
  const configurationKey=JSON.stringify(options.configuration)
  const token=useMemo(()=>({}),[store,configurationKey,options.transientCriteria,lists])
  const live=useRef<object|null>(token);live.current=token
  useLayoutEffect(()=>{live.current=token;return()=>{live.current=null}},[token])
  const current=()=>Boolean(key && live.current===token)
  const [active,setActive]=useState<{key:string|null;id:string}>({key,id:lists?.activePersonalViews.get(definition.id)||''})
  const activeId=active.key===key && snapshot.views.some(view=>view.id===active.id)?active.id:''
  const selected=snapshot.views.find(view=>view.id===activeId)
  const mark=(id:string)=>{setActive({key,id});if(id)lists?.activePersonalViews.set(definition.id,id);else lists?.activePersonalViews.delete(definition.id)}
  const [feedback,setFeedback]=useState<{token:object;text:string;error:boolean}|null>(null)
  const report=(text:string,error=false)=>setFeedback({token:store,text,error})
  const errors:Record<ViewOperationError,string>={ 'invalid-name':'Enter a view name of 1 to 60 characters.', 'duplicate-name':'A personal view already has this name.', 'view-limit':'You can save up to 12 personal views.', 'missing-view':'This personal view is no longer available.' }
  const finish=(error:ViewOperationError|null,text:string)=>{report(error?errors[error]:text,Boolean(error));return !error}
  return { ...snapshot, enabled:Boolean(key), activeId, activeName:selected?.name||'',
    dirty:Boolean(selected && (configurationKey!==JSON.stringify(normalizeViewConfiguration(selected,definition)) || options.transientCriteria)),
    feedback:feedback?.token===store?feedback:null,
    create:(name:string)=>{
      if(!current())return false
      const id=`view-${crypto.randomUUID()}`
      const view:PersonalTableViewV1={version:1,id,name,tableId:definition.id,...options.configuration}
      if(!finish(store.create(view),'Personal view saved.'))return false
      mark(id);return true
    },
    apply:(id:string)=>{
      if(!current())return false
      const original=store.getSnapshot().views.find(view=>view.id===id),view=normalizePersonalView(original,definition)
      if(!view){report('This personal view is no longer available.',true);return false}
      options.apply(normalizeViewConfiguration(view,definition));mark(id)
      report('Personal view applied. Unsupported settings are omitted.');return true
    },
    rename:(name:string)=>{if(!current()||!activeId)return false;return finish(store.rename(activeId,name),'Personal view renamed.')},
    remove:()=>{if(!current()||!activeId)return false;store.remove(activeId);mark('');report('Personal view deleted.');return true},
    restoreDefault:()=>{
      if(!current())return false
      const defaults=normalizeViewConfiguration({},definition)
      // Use the domain defaults, not a remembered query or another view.
      options.apply(defaults);mark('');report('Default view restored.');return true
    },
  }
}
export type PersonalTableViews = ReturnType<typeof usePersonalTableViews>
